import { Outlet, NavLink, Link, useLocation } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { useTranslation } from "react-i18next";
import { MdBusiness, MdSchedule, MdVideocam } from "react-icons/md";
import { AdminDataProvider, useAdminData } from "../../context/AdminDataContext";
import AdminTopBar from "./AdminTopBar";

const AdminLayoutContent = () => {
  const { t } = useTranslation();
  const { accounts } = useMsal();
  const location = useLocation();
  const { loading, error, refresh } = useAdminData();
  const isDataRoute =
    location.pathname === "/admin/buildings" ||
    location.pathname === "/admin/realtime";

  const navLinkClass = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors border-l-4 ${
      isActive
        ? "bg-gray-700/50 border-[#cabb78] text-white"
        : "border-transparent text-gray-300 hover:bg-gray-700 hover:text-white"
    }`;

  return (
    <div className="flex min-h-screen">
      {/* Sidebar - full height */}
      <aside className="w-56 flex-shrink-0 flex flex-col bg-gray-800 border-r border-gray-700 min-h-screen">
        <div className="flex-shrink-0 px-4 pt-5 pb-4 border-b border-gray-700">
          <Link
            to="/"
            className="block text-lg font-bold tracking-tight bg-gradient-to-r from-[#cabb78] to-[#b5a666] bg-clip-text text-transparent hover:opacity-90 transition-opacity"
          >
            Kiinteistömittaus
          </Link>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1" aria-label={t("admin_portal")}>
          <NavLink to="/admin/buildings" className={navLinkClass}>
            <MdBusiness className="w-5 h-5 flex-shrink-0" aria-hidden />
            <span>{t("admin_tab_buildings")}</span>
          </NavLink>
          <NavLink to="/admin/realtime" className={navLinkClass}>
            <MdSchedule className="w-5 h-5 flex-shrink-0" aria-hidden />
            <span>{t("admin_tab_realtime")}</span>
          </NavLink>
          <NavLink to="/admin/camera" className={navLinkClass}>
            <MdVideocam className="w-5 h-5 flex-shrink-0" aria-hidden />
            <span>{t("admin_tab_camera")}</span>
          </NavLink>
        </nav>
      </aside>

      {/* Right column: top bar + main content */}
      <div className="flex-1 flex flex-col min-h-screen min-w-0">
        <AdminTopBar />
        <main className="flex-1 overflow-auto bg-gray-100">
        <div className="p-6 lg:p-8">
          {/* Header - Bress style */}
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 tracking-tight">
                {t("admin_portal")}
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                {t("admin_welcome", {
                  name: accounts[0]?.username ?? "",
                })}
              </p>
            </div>
            <button
              type="button"
              onClick={refresh}
              disabled={loading}
              className="inline-flex items-center px-4 py-2 rounded-xl text-sm font-medium text-white bg-gradient-to-r from-[#cabb78] to-[#b5a666] hover:from-[#b5a666] hover:to-[#cabb78] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              {t("admin_refresh")}
            </button>
          </div>

          {isDataRoute && (loading || error) && (
            <div className="mb-4">
              {loading && (
                <p className="text-gray-500">{t("admin_loading_data")}</p>
              )}
              {error && (
                <p className="text-red-600">
                  {t("admin_error")}: {error}
                </p>
              )}
            </div>
          )}

          <Outlet />
        </div>
      </main>
      </div>
    </div>
  );
};

const AdminLayout = () => {
  return (
    <AdminDataProvider>
      <AdminLayoutContent />
    </AdminDataProvider>
  );
};

export default AdminLayout;
